import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Search, X, Check } from "lucide-react";

/**
 * Reusable typeahead picker hitting `/master/{entity}?search=...`.
 *
 * Props:
 *  - entity: "customers" | "body-shops" | "insurance" | "technicians" | "locations"
 *  - label: visible label
 *  - testId: data-testid prefix
 *  - value: currently selected display text (string)
 *  - onPick(row) — row is the chosen master record { id, name|first_name|..., ... }
 *  - onClear(): user pressed × to remove the selection
 *  - displayKey: function(row) => string; defaults vary per entity
 *  - onCreateNew(query): optional — if provided, shows "+ Create new" inside the dropdown
 */
export default function EntityPicker({
  entity, label, testId, value, onPick, onClear, displayKey, placeholder, required, onCreateNew,
}) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const defaultDisplay = (r) => {
    if (entity === "customers") return `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email || r.phone || r.id;
    if (entity === "body-shops") return r.company_name || r.name || r.id;
    if (entity === "insurance") return r.name || r.short_code || r.id;
    if (entity === "technicians") return r.name || r.email || r.id;
    if (entity === "locations") return r.name || r.address || r.id;
    return r.name || r.id;
  };
  const display = displayKey || defaultDisplay;

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const url = q ? `/master/${entity}?search=${encodeURIComponent(q)}` : `/master/${entity}`;
      api.get(url).then((r) => { setRows(r.data || []); setHighlight(0); }).catch(() => setRows([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q, entity, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const choose = (r) => {
    onPick(r);
    setOpen(false);
    setQ("");
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(rows.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && rows[highlight]) { e.preventDefault(); choose(rows[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const hasSelection = !!value;

  return (
    <div className="relative" ref={wrapRef}>
      {label && (
        <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">
          {label}{required && <span className="text-red-400 ml-1">*</span>}
        </Label>
      )}
      <div className="relative mt-1.5">
        {hasSelection ? (
          <div
            data-testid={`${testId}-selected`}
            className="flex items-center justify-between gap-2 bg-blue-500/10 border border-blue-500/30 rounded-md px-3 h-10 text-sm"
          >
            <span className="flex items-center gap-2 truncate text-blue-200">
              <Check className="w-3.5 h-3.5 shrink-0" />
              {value}
            </span>
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => { onClear && onClear(); setQ(""); }}
              className="text-slate-400 hover:text-red-400 shrink-0"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            <Input
              ref={inputRef}
              data-testid={`${testId}-input`}
              className="pl-9 bg-[#030712] border-slate-800"
              placeholder={placeholder || `Search ${entity.replace("-", " ")}…`}
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKey}
              autoComplete="off"
            />
          </>
        )}
      </div>

      {open && !hasSelection && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-700 bg-[#0B1120] shadow-xl"
        >
          {rows.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {q ? "No matches" : `Type to search ${entity.replace("-", " ")}`}
            </div>
          )}
          {rows.map((r, i) => (
            <button
              key={r.id}
              type="button"
              data-testid={`${testId}-option-${i}`}
              onClick={() => choose(r)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 text-sm border-b border-slate-800/60 last:border-0 ${
                i === highlight ? "bg-blue-500/15 text-blue-200" : "text-slate-300 hover:bg-slate-800/50"
              }`}
            >
              <div className="truncate">{display(r)}</div>
              {(r.phone || r.email || r.short_code || r.city || r.role) && (
                <div className="text-[10px] text-slate-500 truncate mt-0.5">
                  {[r.phone, r.email, r.short_code, r.city, r.role].filter(Boolean).join(" · ")}
                </div>
              )}
            </button>
          ))}
          {onCreateNew && q && (
            <button
              type="button"
              data-testid={`${testId}-create-new`}
              onClick={() => { onCreateNew(q); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-slate-800 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" /> Create new “{q}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
